import { Button, FileButton, Modal, Switch, Text, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { Upload, Video } from "lucide-react";
import React, { useEffect, useState } from "react";
import { addEpisode, updateEpisode } from "../utils/api";
import { videoAPI } from "../services/bunnyApi";
import CustomInput from "./common/Input";
import UserUploadDataFirebase from "./common/userUploadDataFirebase";
import BunnyVideoSelector from "./common/BunnyVideoSelector";

const EpisodeModal = ({ isOpen, onClose, seasonId, onSuccess, episodeToEdit }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [horizontalFile, setHorizontalFile] = useState(null);
  const [verticalFile, setVerticalFile] = useState(null);
  const [isBunnyOpen, setIsBunnyOpen] = useState(false);

  const initialFormState = {
    name: "", description: "", position: "", url: "", duration: "",
    horizontalBanner: null, verticalBanner: null, subtitles: [], isEnable: true,
    videoId: "", mp4UrlArray: "", mp4Url: "",
  };
  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    if (episodeToEdit) {
      setFormData({
        name: episodeToEdit.name, description: episodeToEdit.description,
        position: episodeToEdit.position, url: episodeToEdit.url,
        duration: episodeToEdit.duration, horizontalBanner: episodeToEdit.horizontalBanner,
        verticalBanner: episodeToEdit.verticalBanner, subtitles: episodeToEdit.subtitles || [],
        isEnable: episodeToEdit.isEnable !== undefined ? episodeToEdit.isEnable : true,
        videoId: episodeToEdit.videoId || "",
        mp4UrlArray:
          typeof episodeToEdit.mp4UrlArray === "string"
            ? episodeToEdit.mp4UrlArray
            : episodeToEdit.mp4UrlArray
            ? JSON.stringify(episodeToEdit.mp4UrlArray)
            : "",
        mp4Url: episodeToEdit.mp4Url || "",
      });
    } else {
      setFormData(initialFormState);
      setResetKey((prev) => prev + 1);
      setHorizontalFile(null);
      setVerticalFile(null);
    }
  }, [episodeToEdit, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectBunnyVideo = (video) => {
    const url = videoAPI.getPlaylistUrl(video.guid);
    const mp4UrlMap = videoAPI.getMp4UrlMap(video.guid);
    const mp4Url480p = videoAPI.getMp4Url(video.guid, "480p");
    setFormData((prev) => ({
      ...prev,
      url,
      videoId: video.guid,
      mp4UrlArray: JSON.stringify(mp4UrlMap),
      mp4Url: mp4Url480p,
    }));
    notifications.show({ message: `"${video.title}" selected`, color: "green" });
    setIsBunnyOpen(false);
  };

  const handleSubmit = async () => {
    const requiredFields = ["name", "description", "horizontalBanner", "verticalBanner"];
    const missingFields = requiredFields.filter((field) => !formData[field]);
    if (missingFields.length > 0) {
      notifications.show({ message: `Please fill in all required fields: ${missingFields.join(", ")}`, color: "red" });
      return;
    }

    setIsLoading(true);
    try {
      const submitData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === "subtitles") submitData.append("subtitles", JSON.stringify(value));
        else if (key === "horizontalBanner" && typeof value === "string") submitData.append(key, value);
        else if (key === "verticalBanner" && typeof value === "string") submitData.append(key, value);
        else if (value !== null && value !== undefined && value !== "") submitData.append(key, value);
      });
      submitData.append("season", seasonId);

      const [success, response] = episodeToEdit
        ? await updateEpisode(episodeToEdit._id, submitData)
        : await addEpisode(submitData);

      if (success) {
        notifications.show({ message: `Episode ${episodeToEdit ? "updated" : "created"} successfully`, color: "green" });
        if (!episodeToEdit) { setFormData(initialFormState); setHorizontalFile(null); setVerticalFile(null); }
        onSuccess();
        onClose();
      } else {
        notifications.show({ message: response.message || "Operation failed", color: "red" });
      }
    } catch (error) {
      notifications.show({ message: "An error occurred", color: "red" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal opened={isOpen} onClose={onClose} title={`${episodeToEdit ? "Edit" : "Add"} Episode`} size="md">
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <CustomInput name="Name" value={formData.name} onChange={(e) => handleInputChange("name", e.target.value)} required />
        <CustomInput name="Description" value={formData.description} onChange={(e) => handleInputChange("description", e.target.value)} required />
        <CustomInput name="Episode Number" value={formData.position} onChange={(e) => handleInputChange("position", e.target.value)} type="number" required />
        <div>
          <Text size="sm" fw={500} mb={4}>Video</Text>
          <div className="flex gap-2 items-center">
            <TextInput
              style={{ flex: 1 }}
              readOnly
              value={formData.url}
              placeholder="Select video from Bunny library"
            />
            <Button leftSection={<Video size={16} />} onClick={() => setIsBunnyOpen(true)} variant="default">
              Select Video
            </Button>
          </div>
          {formData.videoId && (
            <Text size="xs" c="dimmed" mt={4}>Video ID: {formData.videoId}</Text>
          )}
        </div>
        <CustomInput name="Duration(in minutes)" value={formData.duration} onChange={(e) => handleInputChange("duration", e.target.value)} placeholder="Duration in minutes" />

        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <div>
            <label className="text-sm font-medium mb-1 block">Subtitles</label>
            <div className="flex flex-col gap-3">
              {(formData.subtitles || []).map((subtitle, index) => (
                <div key={index} className="flex flex-col gap-2 border border-zinc-700 p-3 rounded">
                  <TextInput placeholder="Enter language" value={subtitle.language}
                    onChange={(e) => {
                      const newSubs = [...formData.subtitles];
                      newSubs[index] = { ...newSubs[index], language: e.target.value };
                      handleInputChange("subtitles", newSubs);
                    }} />
                  <UserUploadDataFirebase type="srt" label="Subtitles" name={`subtitles[${index}].link`} update={true}
                    onChange={(url) => {
                      const newSubs = [...formData.subtitles];
                      newSubs[index] = { ...newSubs[index], link: url };
                      handleInputChange("subtitles", newSubs);
                    }} />
                  <Button color="red" variant="subtle" size="xs" fullWidth onClick={() => handleInputChange("subtitles", formData.subtitles.filter((_, i) => i !== index))}>Delete</Button>
                </div>
              ))}
              <Button variant="outline" onClick={() => handleInputChange("subtitles", [...(formData.subtitles || []), { language: "", link: "" }])} fullWidth>Add Subtitle</Button>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <div>
            <label className="text-sm mb-1 block">Upload Horizontal Banner - (1280 x 720) px</label>
            <FileButton key={`horizontal-${resetKey}`} onChange={(file) => { setHorizontalFile(file); handleInputChange("horizontalBanner", file); }} accept="image/*">
              {(props) => <Button variant="default" leftSection={<Upload size={16} />} {...props}>{horizontalFile ? horizontalFile.name : "Upload Horizontal Banner"}</Button>}
            </FileButton>
          </div>
          <div>
            <label className="text-sm mb-1 block">Upload Vertical Banner - (900 x 1200) px</label>
            <FileButton key={`vertical-${resetKey}`} onChange={(file) => { setVerticalFile(file); handleInputChange("verticalBanner", file); }} accept="image/*">
              {(props) => <Button variant="default" leftSection={<Upload size={16} />} {...props}>{verticalFile ? verticalFile.name : "Upload Vertical Banner"}</Button>}
            </FileButton>
          </div>
          <div>
            <label className="text-sm mb-1 block">Enable Episode</label>
            <Switch checked={formData.isEnable} onChange={(e) => handleInputChange("isEnable", e.currentTarget.checked)}
              label={formData.isEnable ? "Enabled" : "Disabled"} />
          </div>
        </div>

        <Button onClick={handleSubmit} loading={isLoading}>{episodeToEdit ? "Update" : "Add"} Episode</Button>
      </div>

      <BunnyVideoSelector
        open={isBunnyOpen}
        onCancel={() => setIsBunnyOpen(false)}
        onSelect={handleSelectBunnyVideo}
        title="Select Video (Bunny)"
        verticalOnly={false}
      />
    </Modal>
  );
};

export default EpisodeModal;
